import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAudit } from '@/contexts/AuditContext';
import { DiscardChangesDialog } from '@/components/settings/DiscardChangesDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldFilterDialog, FieldFilterConfig, FieldFilterType, CustomFieldOption } from '@/components/settings/FieldFilterDialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Check,
  X,
  Eye,
  EyeOff,
  Calendar,
  FileText,
  Users,
  Clock,
  MapPin,
  StickyNote,
  History,
  Star,
  Layers,
  Tag,
  Hash,
  ToggleLeft,
  CalendarDays,
  AlignLeft,
  CheckSquare,
  MessageSquare,
  GripVertical,
  Maximize2,
  Minimize2,
  RotateCcw,
  User,
  FolderOpen,
  Lock,
  Loader2,
  Search,
  ChevronLeft,
  ArrowRight,
  Repeat,
  SlidersHorizontal,
  Briefcase,
  Filter,
} from 'lucide-react';
import { BookingType, FieldConfiguration, TabConfiguration, Resource, PermissionLevel } from '@/types';
import { generateDefaultFieldConfigurations, generateDefaultTabConfigurations, generateDefaultFieldOrder } from '@/utils/field-configurations';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createPortal } from 'react-dom';
import { bookingTypeUsagesApi, BookingTypeUsageDto, projectsApi, EBProjectsCustomFieldDefinition } from '@/services/api';
import { useDataMode } from '@/contexts/DataModeContext';

// ─── Audit Types ──────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  bookingTypeId: string;
  timestamp: string;          // ISO string
  user: string;
  action: 'created' | 'updated' | 'deleted';
  changes: AuditChange[];
}

interface AuditChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface BookingTypeSetupProps {
  bookingTypes: BookingType[];
  bookings: { bookingTypeId?: string }[];
  resources?: Resource[];
  onSave: (bookingType: BookingType) => Promise<void>;
  onDelete: (id: string) => void;
  dataMode?: 'demo' | 'live';
  demoBookingTypeUsages?: BookingTypeUsageDto[];
  onDemoUsageChange?: (bookingTypeId: string, resourceId: string, level: PermissionLevel) => void;
  onDemoBulkUsageChange?: (bookingTypeId: string, resourceIds: string[], level: PermissionLevel) => void;
}

type WizardStep = 1 | 2 | 3 | 4;

type SectionId = 'schedule' | 'resource' | 'project-details';

interface WizardState {
  id?: string;
  name: string;
  description: string;
  code: string;
  active: boolean;
  autoUpdateTimesheet: boolean;
  doNotUpdateTimesheetWithHours: boolean;
  obeyUserWorkingProfileAndBankHolidays: boolean;
  autoAdjustBookingForWorkingProfileAndBankHolidays: boolean;
  addDaysToEndOfBooking: boolean;
  allowRepeatBookings: boolean;
  fieldConfigurations: FieldConfiguration[];
  tabConfigurations: TabConfiguration[];
  fieldOrder: string[];
  sectionOrder: SectionId[];
  customTabFields: string[];      // fields assigned to the Custom Tab
  customTabFieldOrder: string[];  // display order within the Custom Tab
  tabOrder: string[];             // display order of tab accordions
}

// ─── Audit Helpers ────────────────────────────────────────────────────────────

const AUDIT_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  code: 'Internal Code',
  description: 'Description',
  active: 'Status',
  autoUpdateTimesheet: 'Auto-update Timesheet',
  doNotUpdateTimesheetWithHours: 'Do Not Update Timesheet with Hours',
  obeyUserWorkingProfileAndBankHolidays: 'Obey Working Profile & Bank Holidays',
  autoAdjustBookingForWorkingProfileAndBankHolidays: 'Auto-adjust for Working Profile',
  addDaysToEndOfBooking: 'Add Days to End of Booking',
  allowRepeatBookings: 'Allow Repeat Bookings',
  fieldOrder: 'Field Order',
  sectionOrder: 'Section Order',
  tabOrder: 'Tab Order',
};

function formatAuditValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ') || '(empty)';
  return String(value);
}

function diffBookingType(
  before: BookingType | undefined,
  after: BookingType,
  wizardState: WizardState
): AuditChange[] {
  const changes: AuditChange[] = [];

  // Scalar fields
  const scalarKeys: (keyof BookingType)[] = [
    'name', 'code', 'active',
    'autoUpdateTimesheet', 'doNotUpdateTimesheetWithHours',
    'obeyUserWorkingProfileAndBankHolidays',
    'autoAdjustBookingForWorkingProfileAndBankHolidays',
    'addDaysToEndOfBooking', 'allowRepeatBookings',
  ];

  for (const key of scalarKeys) {
    const oldVal = before ? before[key] : undefined;
    const newVal = after[key];
    if (String(oldVal ?? '') !== String(newVal ?? '')) {
      changes.push({
        field: AUDIT_FIELD_LABELS[key] ?? key,
        oldValue: before ? formatAuditValue(key, oldVal) : null,
        newValue: formatAuditValue(key, newVal),
      });
    }
  }

  // Description (stored in wizardState only)
  if ((before as unknown as { description?: string })?.description !== wizardState.description) {
    changes.push({
      field: 'Description',
      oldValue: (before as unknown as { description?: string })?.description || null,
      newValue: wizardState.description || null,
    });
  }

  // Field order
  const oldOrder = (before?.fieldOrder ?? []).join(',');
  const newOrder = (after.fieldOrder ?? []).join(',');
  if (oldOrder !== newOrder) {
    changes.push({
      field: 'Field Order',
      oldValue: before ? (before.fieldOrder ?? []).join(', ') || '(none)' : null,
      newValue: (after.fieldOrder ?? []).join(', ') || '(none)',
    });
  }

  // Section order
  const oldSec = (before?.sectionOrder ?? []).join(',');
  const newSec = (after.sectionOrder ?? []).join(',');
  if (oldSec !== newSec) {
    changes.push({
      field: 'Section Order',
      oldValue: before ? (before.sectionOrder ?? []).join(', ') : null,
      newValue: (after.sectionOrder ?? []).join(', '),
    });
  }

  // Tab order
  const oldTabOrder = (before as unknown as { tabOrder?: string[] })?.tabOrder?.join(',') ?? '';
  const newTabOrder = wizardState.tabOrder.join(',');
  if (oldTabOrder !== newTabOrder) {
    changes.push({
      field: 'Tab Order',
      oldValue: oldTabOrder || null,
      newValue: wizardState.tabOrder.join(', '),
    });
  }

  // Field configurations — per-field changes
  const afterFields = after.fieldConfigurations ?? [];
  const beforeFields = before?.fieldConfigurations ?? [];
  for (const fc of afterFields) {
    const prev = beforeFields.find(f => f.fieldName === fc.fieldName);
    const labelChanged = (prev?.customName ?? '') !== (fc.customName ?? '');
    const visibleChanged = (prev?.isVisible ?? false) !== (fc.isVisible ?? false);
    const reqChanged = (prev?.isCompulsory ?? false) !== (fc.isCompulsory ?? false);
    const tooltipChanged = (prev?.showTooltip ?? false) !== (fc.showTooltip ?? false);
    if (labelChanged) changes.push({ field: `Field "${fc.fieldName}" — Label`, oldValue: prev?.customName || null, newValue: fc.customName || null });
    if (visibleChanged) changes.push({ field: `Field "${fc.fieldName}" — Visible`, oldValue: prev ? String(prev.isVisible) : null, newValue: String(fc.isVisible) });
    if (reqChanged) changes.push({ field: `Field "${fc.fieldName}" — Required`, oldValue: prev ? String(prev.isCompulsory) : null, newValue: String(fc.isCompulsory) });
    if (tooltipChanged) changes.push({ field: `Field "${fc.fieldName}" — Show Tooltip`, oldValue: prev ? String(prev.showTooltip) : null, newValue: String(fc.showTooltip) });
  }

  // Tab configurations — per-tab visibility and label changes
  const afterTabs = after.tabConfigurations ?? [];
  const beforeTabs = before?.tabConfigurations ?? [];
  for (const tc of afterTabs) {
    const prev = beforeTabs.find(t => t.tabName === tc.tabName);
    if ((prev?.isVisible ?? false) !== (tc.isVisible ?? false))
      changes.push({ field: `Tab "${tc.tabName}" — Visible`, oldValue: prev ? String(prev.isVisible) : null, newValue: String(tc.isVisible) });
    if ((prev?.customName ?? '') !== (tc.customName ?? ''))
      changes.push({ field: `Tab "${tc.tabName}" — Label`, oldValue: prev?.customName || null, newValue: tc.customName || null });
  }

  return changes;
}

// ─── Field Groups ─────────────────────────────────────────────────────────────

const FIELD_GROUPS = [
  {
    id: 'core',
    label: 'Core Fields',
    icon: Star,
    color: 'text-amber-500',
    fields: ['Booking Type', 'Status', 'Colour'],
  },
  {
    id: 'project',
    label: 'Project Details',
    icon: Layers,
    color: 'text-blue-500',
    fields: ['Project', 'Cost Centre', 'Customer', 'Cost Centre Task'],
  },
  {
    id: 'booking',
    label: 'Booking Details',
    icon: FileText,
    color: 'text-green-500',
    fields: ['Description', 'Location', 'Details'],
  },
  {
    id: 'user-char',
    label: 'User Char Fields',
    icon: Tag,
    color: 'text-purple-500',
    fields: Array.from({ length: 20 }, (_, i) => `User Char ${i + 1}`),
  },
  {
    id: 'user-number',
    label: 'User Number Fields',
    icon: Hash,
    color: 'text-orange-500',
    fields: Array.from({ length: 10 }, (_, i) => `User Number ${i + 1}`),
  },
  {
    id: 'user-flag',
    label: 'User Flag Fields',
    icon: ToggleLeft,
    color: 'text-red-500',
    fields: Array.from({ length: 10 }, (_, i) => `User Flag ${i + 1}`),
  },
  {
    id: 'user-date',
    label: 'User Date Fields',
    icon: CalendarDays,
    color: 'text-teal-500',
    fields: Array.from({ length: 10 }, (_, i) => `User Date ${i + 1}`),
  },
  {
    id: 'user-notes',
    label: 'User Notes Fields',
    icon: AlignLeft,
    color: 'text-indigo-500',
    fields: Array.from({ length: 5 }, (_, i) => `User Notes ${i + 1}`),
  },
  {
    id: 'user-time',
    label: 'User Time Fields',
    icon: Clock,
    color: 'text-pink-500',
    fields: Array.from({ length: 5 }, (_, i) => `User Time ${i + 1}`),
  },
];

const TAB_ICONS: Record<string, React.ReactNode> = {
  address: <MapPin className="h-3.5 w-3.5" />,
  history: <History className="h-3.5 w-3.5" />,
  'customer-notes': <StickyNote className="h-3.5 w-3.5" />,
  'project-notes': <FileText className="h-3.5 w-3.5" />,
  custom: <CheckSquare className="h-3.5 w-3.5" />,
};

// ─── Live Preview ─────────────────────────────────────────────────────────────

// Renders a single field input mock — used inside the Project Details section
function FieldInputMock({ field, compact }: { field: FieldConfiguration; compact: boolean }) {
  const name = field.fieldName.toLowerCase();
  const label = field.customName || field.fieldName;
  const textXs = compact ? 'text-[9px]' : 'text-[10px]';
  const textInput = compact ? 'text-[10px]' : 'text-[11px]';

  const input = (() => {
    if (['project', 'cost centre', 'customer', 'cost centre task'].some((k) => name === k)) {
      return (
        <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center justify-between', textInput, 'text-muted-foreground')}>
          <span>Select {label.toLowerCase()}...</span>
          <ChevronDown className="h-2.5 w-2.5 flex-shrink-0" />
        </div>
      );
    }
    if (name.includes('description') || name.includes('details') || name.includes('notes')) {
      return (
        <div className={cn('h-9 rounded border border-input bg-background px-1.5 py-1 flex items-start', textInput, 'text-muted-foreground')}>
          Enter {label.toLowerCase()}...
        </div>
      );
    }
    if (name.includes('flag')) {
      return (
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-6 rounded-full bg-muted border border-input flex-shrink-0" />
          <span className={cn(textXs, 'text-muted-foreground')}>{label}</span>
        </div>
      );
    }
    if (name.includes('date')) {
      return (
        <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center gap-1', textInput, 'text-muted-foreground')}>
          <Calendar className="h-2.5 w-2.5 flex-shrink-0" /> Select date
        </div>
      );
    }
    if (name.includes('time')) {
      return (
        <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center gap-1', textInput, 'text-muted-foreground')}>
          <Clock className="h-2.5 w-2.5 flex-shrink-0" /> 00:00
        </div>
      );
    }
    return (
      <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center', textInput, 'text-muted-foreground')}>
        Enter {label.toLowerCase()}...
      </div>
    );
  })();

  return (
    <div>
      <div className={cn(textXs, 'text-muted-foreground mb-0.5 flex items-center gap-0.5')}>
        {label}
        {field.isCompulsory && <span className="text-destructive font-bold">*</span>}
        {field.showTooltip && <MessageSquare className="h-2 w-2 text-primary/60" />}
      </div>
      {input}
    </div>
  );
}

// Sortable field cell inside Project Details (2-column grid)
function SortableFieldCell({
  field,
  onRemove,
  compact,
}: {
  field: FieldConfiguration;
  onRemove: (name: string) => void;
  compact: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.fieldName,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };

  const name = field.fieldName.toLowerCase();
  const isWideField = name.includes('description') || name.includes('details') || name.includes('notes');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('group relative rounded border border-transparent hover:border-primary/30 hover:bg-primary/[0.03] transition-all p-1', isDragging && 'z-50 shadow-md', isWideField && 'col-span-2')}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute left-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none p-0.5 rounded hover:bg-muted"
        title="Drag to reorder"
      >
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </button>
      <div className="pl-3">
        <FieldInputMock field={field} compact={compact} />
      </div>
      {/* Remove */}
      <button
        onClick={() => onRemove(field.fieldName)}
        className="absolute -right-1 -top-1 opacity-0 group-hover:opacity-100 transition-opacity h-3.5 w-3.5 rounded-full bg-destructive/80 hover:bg-destructive flex items-center justify-center"
        title="Remove field"
      >
        <X className="h-2 w-2 text-white" />
      </button>
    </div>
  );
}

// Sortable section wrapper — used for section-level drag-and-drop
function SortableSection({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && 'z-50 shadow-xl rounded-lg')}>
      {/* Pass drag handle props via context — children access via useSortable themselves */}
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<{ dragHandleProps?: object }>, { dragHandleProps: { ...attributes, ...listeners } })
          : child
      )}
    </div>
  );
}

// Accordion section used in the preview
function PreviewAccordion({
  id,
  icon: Icon,
  label,
  isOpen,
  onToggle,
  draggable,
  dragHandleProps,
  compact,
  children,
}: {
  id: string;
  icon: React.ElementType;
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  draggable?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  compact?: boolean;
  children: React.ReactNode;
}) {
  const px = compact ? 'px-2.5' : 'px-3';
  const py = compact ? 'py-1.5' : 'py-2';
  const textSm = compact ? 'text-[10px]' : 'text-xs';
  const iconSm = compact ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <div className="border-b last:border-b-0">
      <div className={cn('flex items-center bg-muted/30 hover:bg-muted/50 transition-colors', px, py)}>
        {/* Drag handle for section reordering */}
        {draggable && (
          <button
            {...(dragHandleProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            className="mr-1.5 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none opacity-40 hover:opacity-80 transition-opacity"
            title="Drag to reorder section"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className={cn(iconSm, 'text-muted-foreground')} />
          </button>
        )}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center justify-between min-w-0"
        >
          <div className="flex items-center gap-1.5">
            <Icon className={cn(iconSm, 'text-primary flex-shrink-0')} />
            <span className={cn(textSm, 'font-semibold truncate')}>{label}</span>
          </div>
          <ChevronDown className={cn(iconSm, 'text-muted-foreground transition-transform flex-shrink-0 ml-1', isOpen && 'rotate-180')} />
        </button>
      </div>
      {isOpen && <div className={cn(px, py)}>{children}</div>}
    </div>
  );
}

// ─── SortableTabRow ───────────────────────────────────────────────────────────
// A sortable wrapper for each tab accordion row in the preview

function SortableTabRow({
  id,
  TabIcon,
  displayName,
  isOpen,
  onToggle,
  compact,
  px,
  py,
  textSm,
  textXs,
  iconSm,
  children,
}: {
  id: string;
  TabIcon: React.ElementType;
  displayName: string;
  isOpen: boolean;
  onToggle: () => void;
  compact: boolean;
  px: string;
  py: string;
  textSm: string;
  textXs: string;
  iconSm: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="border-b last:border-b-0">
      <button
        onClick={onToggle}
        className={cn('w-full flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors', px, py)}
      >
        <div className="flex items-center gap-1.5">
          {/* Drag handle */}
          <span
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/80 transition-colors flex-shrink-0"
          >
            <GripVertical className={iconSm} />
          </span>
          <TabIcon className={cn(iconSm, 'text-primary')} />
          <span className={cn(textSm, 'font-semibold')}>{displayName}</span>
        </div>
        <ChevronDown className={cn(iconSm, 'text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className={cn(px, py)}>
          {children}
        </div>
      )}
    </div>
  );
}

// The actual preview canvas — used both inline and in the expanded dialog
function PreviewCanvas({
  fieldConfigurations,
  tabConfigurations,
  fieldOrder,
  sectionOrder,
  customTabFields,
  customTabFieldOrder,
  onFieldOrderChange,
  onSectionOrderChange,
  onFieldRemove,
  onCustomTabFieldOrderChange,
  tabOrder,
  onTabOrderChange,
  compact = false,
}: {
  fieldConfigurations: FieldConfiguration[];
  tabConfigurations: TabConfiguration[];
  fieldOrder: string[];
  sectionOrder: SectionId[];
  customTabFields: string[];
  customTabFieldOrder: string[];
  onFieldOrderChange: (newOrder: string[]) => void;
  onSectionOrderChange: (newOrder: SectionId[]) => void;
  onFieldRemove: (fieldName: string) => void;
  onCustomTabFieldOrderChange: (newOrder: string[]) => void;
  tabOrder: string[];
  onTabOrderChange: (newOrder: string[]) => void;
  compact?: boolean;
}) {
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(
    new Set(['schedule', 'resource', 'project-details'])
  );

  const fieldSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const toggleAccordion = (id: string) => {
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fieldOrder.indexOf(active.id as string);
    const newIndex = fieldOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onFieldOrderChange(arrayMove(fieldOrder, oldIndex, newIndex));
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sectionOrder.indexOf(active.id as SectionId);
    const newIndex = sectionOrder.indexOf(over.id as SectionId);
    if (oldIndex === -1 || newIndex === -1) return;
    onSectionOrderChange(arrayMove(sectionOrder, oldIndex, newIndex));
  };

  // Fields in Project Details = enabled fields that are NOT assigned to the Custom Tab
  // and NOT pinned-section fields (Status, Colour live in the top Booking Type bar)
  const PINNED_FIELDS = ['Booking Type', 'Status', 'Colour', 'From', 'To', 'Booking Reference'];
  const isUserField = (name: string) => /^User (Char|Number|Flag|Date|Notes|Time) \d+$/.test(name);
  const orderedVisibleFields = fieldOrder
    .map((name) => fieldConfigurations.find((f) => f.fieldName === name))
    .filter((f): f is FieldConfiguration =>
      !!f && f.isVisible && !customTabFields.includes(f.fieldName) &&
      !PINNED_FIELDS.includes(f.fieldName) && !isUserField(f.fieldName)
    );

  // User-defined fields shown in a separate "Additional Fields" accordion
  const orderedUserFields = fieldOrder
    .map((name) => fieldConfigurations.find((f) => f.fieldName === name))
    .filter((f): f is FieldConfiguration =>
      !!f && f.isVisible && !customTabFields.includes(f.fieldName) &&
      isUserField(f.fieldName)
    );

  // Fields inside the Custom Tab accordion (in their own order)
  const orderedCustomTabFields = customTabFieldOrder
    .map((name) => fieldConfigurations.find((f) => f.fieldName === name))
    .filter((f): f is FieldConfiguration => !!f);

  const visibleTabs = tabConfigurations.filter((t) => t.isVisible);
  const customTabVisible = visibleTabs.some((t) => t.tabName === 'custom');

  const handleCustomTabDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = customTabFieldOrder.indexOf(active.id as string);
    const newIndex = customTabFieldOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onCustomTabFieldOrderChange(arrayMove(customTabFieldOrder, oldIndex, newIndex));
  };

  const handleTabDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tabOrder.indexOf(active.id as string);
    const newIndex = tabOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onTabOrderChange(arrayMove(tabOrder, oldIndex, newIndex));
  };

  const px = compact ? 'px-2.5' : 'px-3';
  const py = compact ? 'py-1.5' : 'py-2';
  const textSm = compact ? 'text-[10px]' : 'text-xs';
  const textXs = compact ? 'text-[9px]' : 'text-[10px]';
  const iconSm = compact ? 'h-2.5 w-2.5' : 'h-3 w-3';

  // ── Section renderers ──────────────────────────────────────────────────────

  const renderSchedule = (dragHandleProps?: object) => (
    <PreviewAccordion
      id="schedule"
      icon={Clock}
      label="Schedule"
      isOpen={openAccordions.has('schedule')}
      onToggle={() => toggleAccordion('schedule')}
      draggable
      dragHandleProps={dragHandleProps as React.HTMLAttributes<HTMLButtonElement>}
      compact={compact}
    >
      <div className="space-y-2">
        {/* Duration + Repeat row */}
        <div className="grid gap-x-2" style={{ gridTemplateColumns: '1fr auto' }}>
          <div>
            <div className={cn(textXs, 'text-muted-foreground mb-0.5')}>Duration <span className="text-destructive">*</span></div>
            <div className="grid grid-cols-4 gap-1.5">
              {([
                { bold: 'Full Day', sub: '00:00 \u2013 23:59', active: true },
                { bold: 'Morning', sub: '00:00 \u2013 11:59', active: false },
                { bold: 'Afternoon', sub: '12:00 \u2013 23:59', active: false },
                { bold: 'Hourly', sub: 'Custom', active: false },
              ]).map(({ bold, sub, active }) => (
                <div
                  key={bold}
                  className={cn(
                    'flex flex-col items-center justify-center rounded-md border px-1 py-1.5 text-center',
                    active
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-background text-foreground'
                  )}
                >
                  <span className={cn('text-[10px] font-semibold leading-tight', active ? 'text-primary' : 'text-foreground')}>{bold}</span>
                  <span className={cn('text-[9px] leading-tight mt-0.5', active ? 'text-primary/70' : 'text-muted-foreground')}>{sub}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Repeat card */}
          <div className="flex flex-col min-w-[52px]">
            <div className={cn(textXs, 'text-muted-foreground mb-0.5')}>Repeat</div>
            <div className="flex flex-1 flex-col items-center justify-center rounded-md border border-dashed border-border bg-background px-2 text-center gap-0.5">
              <Repeat className="h-3 w-3 text-primary/70" />
              <span className={cn('text-[10px] font-semibold leading-tight text-foreground')}>Set</span>
            </div>
          </div>
        </div>
        {/* Date pickers */}
        <div className="flex items-end gap-2">
          <div>
            <div className={cn(textXs, 'text-muted-foreground mb-0.5')}>Start date <span className="text-destructive">*</span></div>
            <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center gap-1', textXs, 'text-muted-foreground')}>
              <CalendarDays className="h-2.5 w-2.5 flex-shrink-0" /> Select date
            </div>
          </div>
          <div className="flex items-center h-6">
            <ArrowRight className="h-2.5 w-2.5 text-muted-foreground/50 flex-shrink-0" />
          </div>
          <div>
            <div className={cn(textXs, 'text-muted-foreground mb-0.5')}>End date <span className="text-destructive">*</span></div>
            <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center gap-1', textXs, 'text-muted-foreground')}>
              <CalendarDays className="h-2.5 w-2.5 flex-shrink-0" /> Select date
            </div>
          </div>
        </div>
      </div>
    </PreviewAccordion>
  );

  const renderResource = (dragHandleProps?: object) => (
    <PreviewAccordion
      id="resource"
      icon={Users}
      label="Resources"
      isOpen={openAccordions.has('resource')}
      onToggle={() => toggleAccordion('resource')}
      draggable
      dragHandleProps={dragHandleProps as React.HTMLAttributes<HTMLButtonElement>}
      compact={compact}
    >
      <div className="grid grid-cols-2 gap-2">
        {/* Resource selector */}
        <div className="space-y-0.5">
          <div className="flex items-center justify-between">
            <div className={cn(textXs, 'text-muted-foreground')}>Resource</div>
            <div className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-primary/40 text-primary bg-primary/5', 'text-[9px] font-medium')}>
              Suggest Resource
            </div>
          </div>
          <div className={cn('h-7 rounded border border-input bg-background px-2 flex items-center justify-between', textXs, 'text-muted-foreground')}>
            <div className="flex items-center gap-1.5">
              <User className="h-2.5 w-2.5" />
              <span>Select resources...</span>
            </div>
            <ChevronDown className="h-2.5 w-2.5" />
          </div>
        </div>
        {/* Role selector */}
        <div className="flex flex-col justify-end space-y-0.5">
          <div className={cn(textXs, 'text-muted-foreground')}>Role</div>
          <div className={cn('h-7 rounded border border-input bg-background px-2 flex items-center justify-between', textXs, 'text-muted-foreground')}>
            <span>Select role...</span>
            <ChevronDown className="h-2.5 w-2.5" />
          </div>
        </div>
      </div>
    </PreviewAccordion>
  );

  const renderProjectDetails = (dragHandleProps?: object) => {
    if (orderedVisibleFields.length === 0) return null;
    return (
    <PreviewAccordion
      id="project-details"
      icon={Briefcase}
      label="Booking Details"
      isOpen={openAccordions.has('project-details')}
      onToggle={() => toggleAccordion('project-details')}
      draggable
      dragHandleProps={dragHandleProps as React.HTMLAttributes<HTMLButtonElement>}
      compact={compact}
    >
      <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
        <SortableContext items={orderedVisibleFields.map((f) => f.fieldName)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            {orderedVisibleFields.map((field) => (
              <SortableFieldCell
                key={field.fieldName}
                field={field}
                onRemove={onFieldRemove}
                compact={compact}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </PreviewAccordion>
    );
  };

  const sectionRenderers: Record<SectionId, (dragHandleProps?: object) => React.ReactNode> = {
    schedule: renderSchedule,
    resource: renderResource,
    'project-details': renderProjectDetails,
  };

  return (
    <div className="bg-background border rounded-lg shadow-sm overflow-hidden flex flex-col h-full text-foreground">
      {/* ── Form header ── */}
      <div className="border-b px-3 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground">
            <ArrowLeft className={iconSm} />
          </div>
          <div className="h-5 w-5 rounded-md bg-primary/10 flex items-center justify-center">
            <CalendarDays className={cn(iconSm, 'text-primary')} />
          </div>
          <span className={cn(textSm, 'font-semibold')}>Add Booking</span>
        </div>
        <Badge variant="secondary" className="text-[10px] h-4 px-1.5">Confirmed</Badge>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ── Section 1: Pinned top fields (Booking Type, Status, Colour) — no section header ── */}
        {(() => {
          const bookingTypeCfg = fieldConfigurations.find((f) => f.fieldName === 'Booking Type');
          const statusCfg  = fieldConfigurations.find((f) => f.fieldName === 'Status');
          const colourCfg  = fieldConfigurations.find((f) => f.fieldName === 'Colour');
          const bookingTypeVisible = bookingTypeCfg?.isVisible ?? true;
          const statusVisible = statusCfg?.isVisible ?? true;
          const colourVisible = colourCfg?.isVisible ?? true;
          const bookingTypeLabel = bookingTypeCfg?.customName || 'Booking Type';
          const statusLabel  = statusCfg?.customName  || 'Status';
          const colourLabel  = colourCfg?.customName  || 'Colour';
          const bookingTypeRequired = bookingTypeCfg?.isCompulsory ?? true;
          const statusRequired = statusCfg?.isCompulsory ?? false;
          const colourRequired = colourCfg?.isCompulsory ?? false;
          const cols = [
            ...(bookingTypeVisible ? [{ key: 'booking-type', label: bookingTypeLabel, required: bookingTypeRequired }] : []),
            ...(statusVisible ? [{ key: 'status', label: statusLabel, required: statusRequired }] : []),
            ...(colourVisible ? [{ key: 'colour', label: colourLabel, required: colourRequired }] : []),
          ];
          if (cols.length === 0) return null;
          return (
            <div className={cn('border-b', px, py, 'grid gap-2')} style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}>
              {cols.map((col) => (
                <div key={col.key}>
                  <div className={cn(textXs, 'text-muted-foreground mb-0.5')}>
                    {col.label}{col.required && <span className="text-destructive"> *</span>}
                  </div>
                  <div className={cn('h-6 rounded border border-input bg-background px-1.5 flex items-center justify-between', textXs, 'text-muted-foreground')}>
                    {col.key === 'status' ? (
                      <><div className="flex items-center gap-1"><div className="h-1.5 w-1.5 rounded-full bg-emerald-500" /><span>Confirmed</span></div><ChevronDown className="h-2 w-2" /></>
                    ) : col.key === 'booking-type' ? (
                      <><span>Select type...</span><ChevronDown className="h-2 w-2" /></>
                    ) : (
                      <><span>None</span><ChevronDown className="h-2 w-2" /></>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── Sections 2–4: reorderable via DnD ── */}
        <DndContext sensors={sectionSensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
          <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
            {sectionOrder.map((sectionId) => {
              const rendered = sectionRenderers[sectionId]();
              if (!rendered) return null;
              return (
                <SortableSection key={sectionId} id={sectionId}>
                  {rendered as React.ReactElement}
                </SortableSection>
              );
            })}
          </SortableContext>
        </DndContext>

        {/* ── Additional Fields section (user-defined fields) ── */}
        {orderedUserFields.length > 0 && (
          <PreviewAccordion
            id="additional-fields"
            icon={SlidersHorizontal}
            label="Additional Fields"
            isOpen={openAccordions.has('additional-fields')}
            onToggle={() => toggleAccordion('additional-fields')}
            compact={compact}
          >
            <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
              <SortableContext items={orderedUserFields.map((f) => f.fieldName)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                  {orderedUserFields.map((field) => (
                    <SortableFieldCell
                      key={field.fieldName}
                      field={field}
                      onRemove={onFieldRemove}
                      compact={compact}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </PreviewAccordion>
        )}

        {/* ── Tab sections as sortable accordions ── */}
        {(() => {
          const tabIconMap: Record<string, React.ElementType> = {
            address: MapPin,
            history: History,
            'customer-notes': StickyNote,
            'project-notes': FolderOpen,
            custom: CheckSquare,
          };
          // Render in tabOrder, only visible tabs (excluding customer-notes)
          const orderedVisibleTabNames = tabOrder.filter((name) =>
            name !== 'customer-notes' && visibleTabs.some((t) => t.tabName === name)
          );
          if (orderedVisibleTabNames.length === 0) return null;
          return (
            <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
              <SortableContext items={orderedVisibleTabNames} strategy={verticalListSortingStrategy}>
                {orderedVisibleTabNames.map((tabName) => {
                  const tab = visibleTabs.find((t) => t.tabName === tabName)!;
                  const TabIcon = tabIconMap[tab.tabName] ?? FileText;
                  const displayName = tab.customName || tab.defaultName;
                  const isOpen = openAccordions.has(tab.tabName);
                  return (
                    <SortableTabRow
                      key={tab.tabName}
                      id={tab.tabName}
                      TabIcon={TabIcon}
                      displayName={displayName}
                      isOpen={isOpen}
                      onToggle={() => toggleAccordion(tab.tabName)}
                      compact={compact}
                      px={px}
                      py={py}
                      textSm={textSm}
                      textXs={textXs}
                      iconSm={iconSm}
                    >
                      {tab.tabName === 'custom' ? (
                        orderedCustomTabFields.length > 0 ? (
                          <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleCustomTabDragEnd}>
                            <SortableContext items={customTabFieldOrder} strategy={verticalListSortingStrategy}>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                {orderedCustomTabFields.map((field) => (
                                  <SortableFieldCell
                                    key={field.fieldName}
                                    field={field}
                                    onRemove={onFieldRemove}
                                    compact={compact}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        ) : (
                          <p className={cn(textXs, 'text-muted-foreground italic text-center py-1')}>
                            No fields assigned — select fields in the Custom Tab config
                          </p>
                        )
                      ) : (
                        <p className={cn(textXs, 'text-muted-foreground italic')}>
                          {tab.tabName === 'address' && 'Address & contact fields'}
                          {tab.tabName === 'history' && 'Booking change history'}
                          {tab.tabName === 'customer-notes' && 'Notes visible to customers'}
                          {tab.tabName === 'project-notes' && 'Internal project notes'}
                        </p>
                      )}
                    </SortableTabRow>
                  );
                })}
              </SortableContext>
            </DndContext>
          );
        })()}
      </div>

      {/* ── Footer ── */}
      <div className={cn('border-t bg-muted/20 flex justify-end gap-1.5 flex-shrink-0', px, py)}>
        <div className={cn('h-6 px-2 rounded border border-input bg-background flex items-center gap-1', textXs, 'text-muted-foreground')}>
          <X className="h-2.5 w-2.5" /> Cancel
        </div>
        <div className={cn('h-6 px-2 rounded bg-primary flex items-center gap-1', textXs, 'text-primary-foreground')}>
          <Check className="h-2.5 w-2.5" /> Save
        </div>
      </div>
    </div>
  );
}

function LiveFormPreview({
  fieldConfigurations,
  tabConfigurations,
  fieldOrder,
  sectionOrder,
  customTabFields,
  customTabFieldOrder,
  tabOrder,
  onFieldOrderChange,
  onSectionOrderChange,
  onFieldRemove,
  onCustomTabFieldOrderChange,
  onTabOrderChange,
  onReset,
}: {
  fieldConfigurations: FieldConfiguration[];
  tabConfigurations: TabConfiguration[];
  fieldOrder: string[];
  sectionOrder: SectionId[];
  customTabFields: string[];
  customTabFieldOrder: string[];
  tabOrder: string[];
  onFieldOrderChange: (newOrder: string[]) => void;
  onSectionOrderChange: (newOrder: SectionId[]) => void;
  onFieldRemove: (fieldName: string) => void;
  onCustomTabFieldOrderChange: (newOrder: string[]) => void;
  onTabOrderChange: (newOrder: string[]) => void;
  onReset: () => void;
}) {
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  return (
    <>
      {/* Reset confirmation dialog */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset preview to defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all field selections, clear any custom ordering, and reset tab assignments back to their defaults. Any changes you've made to the form layout will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { onReset(); setResetConfirmOpen(false); }}
            >
              Reset to defaults
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Expand / Reset button row */}
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] text-muted-foreground">
          <GripVertical className="inline h-2.5 w-2.5" /> drag sections or fields · <X className="inline h-2.5 w-2.5" /> remove field
        </p>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={() => setResetConfirmOpen(true)}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">Reset preview</TooltipContent>
            </Tooltip>

          </div>
        </TooltipProvider>
      </div>

      {/* Inline preview */}
      <div className="flex-1 overflow-hidden relative">
        <div className="absolute inset-0 overflow-y-auto">
          <div className="w-full max-w-[50vw] mx-auto">
          <PreviewCanvas
            fieldConfigurations={fieldConfigurations}
            tabConfigurations={tabConfigurations}
            fieldOrder={fieldOrder}
            sectionOrder={sectionOrder}
            customTabFields={customTabFields}
            customTabFieldOrder={customTabFieldOrder}
            tabOrder={tabOrder}
            onFieldOrderChange={onFieldOrderChange}
            onSectionOrderChange={onSectionOrderChange}
            onFieldRemove={onFieldRemove}
            onCustomTabFieldOrderChange={onCustomTabFieldOrderChange}
            onTabOrderChange={onTabOrderChange}
          />
          </div>
        </div>
      </div>

    </>
  );
}

// ─── Step 1: Name & Identity ──────────────────────────────────────────────────

function Step1({ state, onChange, errors }: { state: WizardState; onChange: (updates: Partial<WizardState>) => void; errors?: Record<string, string> }) {
  // Derive code live from name — kept in sync via onChange so downstream steps can read it
  const derivedCode = state.name.trim()
    ? state.name.trim().slice(0, 8).toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '')
    : '';

  React.useEffect(() => {
    onChange({ code: derivedCode });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedCode]);

  return (
    <div className="space-y-6 max-w-lg mx-auto px-4">
      <div className="text-center space-y-1">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-2">
          <Tag className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">Name & Identity</h3>
        <p className="text-sm text-muted-foreground">Give your booking type a clear, descriptive name</p>
      </div>

      <div className="space-y-4">

        {/* Auto-generated internal code — read-only, above the name */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground uppercase tracking-wide">Internal Code</Label>
          <div className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-md border border-dashed bg-muted/40 select-none',
            errors?.code ? 'border-destructive' : 'border-border'
          )}>
            <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="font-mono text-sm text-muted-foreground tracking-wider flex-1">
              {derivedCode || <span className="italic opacity-50">auto-generated from name</span>}
            </span>
            <span className="text-[10px] text-muted-foreground/60 shrink-0">read-only</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Automatically generated from the booking type name — used for internal reference
          </p>
          {errors?.code && <p className="text-xs text-destructive">{errors.code}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="bt-name">
            Booking Type Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="bt-name"
            value={state.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. Project Work, Internal Meeting, Training"
            className={cn('text-base', errors?.name && 'border-destructive focus-visible:ring-destructive')}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            This name will appear in booking forms and reports
          </p>
          {errors?.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="bt-description">Description</Label>
          <Textarea
            id="bt-description"
            value={state.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Optional description or internal notes about this booking type..."
            rows={3}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: General Options ──────────────────────────────────────────────────

function Step2({ state, onChange }: { state: WizardState; onChange: (updates: Partial<WizardState>) => void }) {
  const [openSection, setOpenSection] = useState<string | null>('timesheet');

  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  return (
    <div className="space-y-4 max-w-2xl mx-auto px-4">
      <div className="text-center space-y-1 mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-2">
          <Layers className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">General Options</h3>
        <p className="text-sm text-muted-foreground">Configure the behaviour of this booking type</p>
      </div>

      {/* Timesheet Section */}
      <Card>
        <Collapsible open={openSection === 'timesheet'} onOpenChange={() => toggleSection('timesheet')}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-semibold">Timesheet Integration</CardTitle>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', openSection === 'timesheet' && 'rotate-180')} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-normal">Auto Update Timesheet</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Automatically update timesheets when bookings are created or modified</p>
                </div>
                <Switch
                  checked={state.autoUpdateTimesheet}
                  onCheckedChange={(v) => onChange({ autoUpdateTimesheet: v })}
                />
              </div>
              {state.autoUpdateTimesheet && (
                <div className="flex items-center justify-between pl-4 border-l-2 border-primary/20">
                  <div>
                    <Label className="text-sm font-normal">Do Not Update Timesheet with Hours</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Update timesheet entries but exclude hour values</p>
                  </div>
                  <Switch
                    checked={state.doNotUpdateTimesheetWithHours}
                    onCheckedChange={(v) => onChange({ doNotUpdateTimesheetWithHours: v })}
                  />
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Working Profile Section */}
      <Card>
        <Collapsible open={openSection === 'working'} onOpenChange={() => toggleSection('working')}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-semibold">Working Profile & Bank Holidays</CardTitle>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', openSection === 'working' && 'rotate-180')} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-normal">Obey User Working Profile and Bank Holidays</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Respect user working hours and bank holiday schedules</p>
                </div>
                <Switch
                  checked={state.obeyUserWorkingProfileAndBankHolidays}
                  onCheckedChange={(v) => {
                    if (!v) {
                      onChange({ obeyUserWorkingProfileAndBankHolidays: false, autoAdjustBookingForWorkingProfileAndBankHolidays: false, addDaysToEndOfBooking: false });
                    } else {
                      onChange({ obeyUserWorkingProfileAndBankHolidays: true });
                    }
                  }}
                />
              </div>
              <div className={cn("flex items-center justify-between pl-4 border-l-2 border-primary/20", !state.obeyUserWorkingProfileAndBankHolidays && "opacity-50")}>
                <div>
                  <Label className="text-sm font-normal">Auto Adjust Booking for Working Profile and Bank Holidays</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Automatically shift bookings around non-working days</p>
                </div>
                <Switch
                  checked={state.autoAdjustBookingForWorkingProfileAndBankHolidays}
                  disabled={!state.obeyUserWorkingProfileAndBankHolidays}
                  onCheckedChange={(v) => {
                    if (!v) {
                      onChange({ autoAdjustBookingForWorkingProfileAndBankHolidays: false, addDaysToEndOfBooking: false });
                    } else {
                      onChange({ autoAdjustBookingForWorkingProfileAndBankHolidays: true });
                    }
                  }}
                />
              </div>
              <div className={cn("flex items-center justify-between pl-4 border-l-2 border-primary/20", (!state.obeyUserWorkingProfileAndBankHolidays || !state.autoAdjustBookingForWorkingProfileAndBankHolidays) && "opacity-50")}>
                <div>
                  <Label className="text-sm font-normal">Add Days to End of Booking</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Extend booking end date to compensate for non-working days</p>
                </div>
                <Switch
                  checked={state.addDaysToEndOfBooking}
                  disabled={!state.obeyUserWorkingProfileAndBankHolidays || !state.autoAdjustBookingForWorkingProfileAndBankHolidays}
                  onCheckedChange={(v) => onChange({ addDaysToEndOfBooking: v })}
                />
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Recurrence Section */}
      <Card>
        <Collapsible open={openSection === 'recurrence'} onOpenChange={() => toggleSection('recurrence')}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-semibold">Recurrence</CardTitle>
                </div>
                <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', openSection === 'recurrence' && 'rotate-180')} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-normal">Allow Repeat Bookings</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Enable recurring/repeating bookings for this type</p>
                </div>
                <Switch
                  checked={state.allowRepeatBookings}
                  onCheckedChange={(v) => onChange({ allowRepeatBookings: v })}
                />
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
}

// ─── Step 3: Field Selection & Live Preview ───────────────────────────────────

function Step3({
  state,
  onChange,
}: {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [filterDialogField, setFilterDialogField] = useState<string | null>(null);
  const [customFieldDefs, setCustomFieldDefs] = useState<EBProjectsCustomFieldDefinition[]>([]);
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  const { getScreenMode } = useDataMode();
  const isLive = getScreenMode('settings') === 'live';

  // Map field names to their filter type for the FieldFilterDialog
  // Only Project supports field filtering for now
  const FILTERABLE_FIELDS: Record<string, FieldFilterType> = {
    'Project': 'project',
  };

  // Fetch custom field definitions only when the filter dialog opens for a project field
  useEffect(() => {
    if (!filterDialogField || !isLive) return;
    if (filterDialogField !== 'Project') return;

    setLoadingCustomFields(true);
    projectsApi.getCustomFieldDefinitionsFromEBProjects()
      .then(defs => setCustomFieldDefs(defs.filter(d => d.enabled)))
      .catch(() => setCustomFieldDefs([]))
      .finally(() => setLoadingCustomFields(false));
  }, [filterDialogField, isLive]);

  const customFieldOptions: CustomFieldOption[] = customFieldDefs.map(d => ({
    value: `customField:${d.id}`,
    label: d.label,
    fieldType: d.fieldType,
    options: d.options,
  }));

  const toggleGroup = (groupId: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  const getFieldConfig = (fieldName: string) =>
    state.fieldConfigurations.find((f) => f.fieldName === fieldName);

  const isFieldVisible = (fieldName: string) =>
    getFieldConfig(fieldName)?.isVisible ?? false;

  const isAlwaysOn = (fieldName: string) =>
    ['From', 'To'].includes(fieldName);

  const isDisabledByParent = (fieldName: string): boolean => {
    if (fieldName === 'Cost Centre') return !isFieldVisible('Project');
    if (fieldName === 'Cost Centre Task') return !isFieldVisible('Cost Centre');
    return false;
  };

  const updateField = (fieldName: string, updates: Partial<FieldConfiguration>) => {
    onChange({
      fieldConfigurations: state.fieldConfigurations.map((f) =>
        f.fieldName === fieldName ? { ...f, ...updates } : f
      ),
    });
  };

  const toggleField = (fieldName: string) => {
    if (isAlwaysOn(fieldName)) return;
    if (isDisabledByParent(fieldName)) return;
    const nowVisible = !isFieldVisible(fieldName);
    updateField(fieldName, { isVisible: nowVisible });
    // Keep fieldOrder in sync: add when enabling, remove when disabling
    if (nowVisible) {
      if (!state.fieldOrder.includes(fieldName)) {
        onChange({ fieldOrder: [...state.fieldOrder, fieldName] });
      }
    } else {
      // Cascade hide: hiding Project hides Cost Centre, hiding Cost Centre hides Cost Centre Task
      const fieldsToHide = [fieldName];
      if (fieldName === 'Project' && isFieldVisible('Cost Centre')) {
        fieldsToHide.push('Cost Centre');
      }
      if ((fieldName === 'Project' || fieldName === 'Cost Centre') && isFieldVisible('Cost Centre Task')) {
        fieldsToHide.push('Cost Centre Task');
      }
      if (fieldsToHide.length > 1) {
        onChange({
          fieldConfigurations: state.fieldConfigurations.map((f) =>
            fieldsToHide.includes(f.fieldName) ? { ...f, isVisible: false } : f
          ),
          fieldOrder: state.fieldOrder.filter((n) => !fieldsToHide.includes(n)),
        });
        return;
      }
      onChange({ fieldOrder: state.fieldOrder.filter((n) => n !== fieldName) });
    }
  };

  const filteredGroups = FIELD_GROUPS.map((group) => ({
    ...group,
    fields: group.fields.filter((f) => f.toLowerCase().includes(search.toLowerCase())),
  })).filter((g) => g.fields.length > 0);

  const visibleCount = state.fieldConfigurations.filter((f) => f.isVisible).length;

  return (
    <div className="flex gap-4 h-full min-h-[580px]">
      {/* Left Panel – Field Palette */}
      <div className="w-72 flex-shrink-0 flex flex-col border rounded-lg overflow-hidden">
        <div className="bg-muted/50 px-3 py-2 border-b">
          <h4 className="text-xs font-semibold text-foreground mb-1.5">Field Palette</h4>
          <Input
            placeholder="Search fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredGroups.map((group) => {
            const Icon = group.icon;
            const isOpen = openGroups.has(group.id);
            const enabledCount = group.fields.filter(isFieldVisible).length;
            return (
              <div key={group.id} className="border-b last:border-b-0">
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className={cn('h-3.5 w-3.5', group.color)} />
                    <span className="text-xs font-medium">{group.label}</span>
                    {enabledCount > 0 && (
                      <Badge variant="secondary" className="h-4 px-1 text-[10px]">{enabledCount}</Badge>
                    )}
                  </div>
                  <ChevronRight className={cn('h-3 w-3 text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                </button>

                {/* Field rows */}
                {isOpen && (
                  <div className="pb-1">
                    {group.fields.map((fieldName) => {
                      const cfg = getFieldConfig(fieldName);
                      const visible = cfg?.isVisible ?? false;
                      const alwaysOn = isAlwaysOn(fieldName);
                      const disabledByParent = isDisabledByParent(fieldName);
                      const isBookingType = fieldName === 'Booking Type';

                      return (
                        <React.Fragment key={fieldName}>
                        <div
                          className={cn(
                            'flex items-center gap-2 px-2.5 py-1.5 border-b border-border/40 last:border-b-0 transition-colors',
                            visible ? 'bg-primary/[0.03]' : 'hover:bg-muted/30',
                          )}
                        >
                          {/* Visibility toggle */}
                          <button
                            onClick={() => toggleField(fieldName)}
                            disabled={alwaysOn || disabledByParent}
                            className={cn('flex-shrink-0', (alwaysOn || disabledByParent) && 'opacity-40 cursor-not-allowed')}
                            title={alwaysOn ? 'Always visible' : disabledByParent ? `Enable ${fieldName === 'Cost Centre' ? 'Project' : 'Cost Centre'} first` : visible ? 'Hide field' : 'Show field'}
                          >
                            {visible
                              ? <Eye className="h-3.5 w-3.5 text-primary" />
                              : <EyeOff className="h-3.5 w-3.5 text-muted-foreground/40" />}
                          </button>

                          {/* Inline label input — editable only when visible */}
                          {visible ? (
                            <Input
                              value={cfg?.customName ?? ''}
                              onChange={(e) => updateField(fieldName, { customName: e.target.value })}
                              placeholder={fieldName}
                              className="h-6 text-[11px] px-1.5 flex-1 min-w-0"
                            />
                          ) : (
                            <span
                              className="flex-1 text-xs text-muted-foreground truncate cursor-pointer select-none"
                              onClick={() => toggleField(fieldName)}
                            >
                              {fieldName}
                            </span>
                          )}

                          {/* Required pill — only when visible */}
                          {visible && (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {isBookingType ? (
                                    <div className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] select-none bg-destructive/10 border-destructive/30 text-destructive opacity-60 cursor-not-allowed">
                                      <span className="font-bold leading-none">*</span>
                                      <Lock className="h-2.5 w-2.5" />
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => updateField(fieldName, { isCompulsory: !cfg?.isCompulsory })}
                                      className={cn(
                                        'flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] transition-colors',
                                        cfg?.isCompulsory
                                          ? 'bg-destructive/10 border-destructive/30 text-destructive'
                                          : 'bg-background border-border text-muted-foreground/50 hover:border-muted-foreground/40'
                                      )}
                                    >
                                      <span className="font-bold leading-none">*</span>
                                    </button>
                                  )}
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs">
                                  {isBookingType ? 'Always required' : cfg?.isCompulsory ? 'Remove required' : 'Mark as required'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {/* Tooltip pill — only when visible */}
                          {visible && (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => updateField(fieldName, { showTooltip: !cfg?.showTooltip })}
                                    className={cn(
                                      'flex-shrink-0 flex items-center px-1.5 py-0.5 rounded border text-[10px] transition-colors',
                                      cfg?.showTooltip
                                        ? 'bg-primary/10 border-primary/30 text-primary'
                                        : 'bg-background border-border text-muted-foreground/50 hover:border-muted-foreground/40'
                                    )}
                                  >
                                    <MessageSquare className="h-3 w-3" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs">
                                  {cfg?.showTooltip ? 'Remove from tooltip' : 'Show on timeline tooltip'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {/* Filter button — only for filterable project-detail fields when visible */}
                          {visible && FILTERABLE_FIELDS[fieldName] && (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => setFilterDialogField(fieldName)}
                                    className={cn(
                                      'flex-shrink-0 flex items-center px-1.5 py-0.5 rounded border text-[10px] transition-colors relative',
                                      cfg?.filterConfig?.enabled
                                        ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-950/30 dark:border-amber-700/50 dark:text-amber-400'
                                        : 'bg-background border-border text-muted-foreground/50 hover:border-muted-foreground/40'
                                    )}
                                  >
                                    <Filter className="h-3 w-3" />
                                    {cfg?.filterConfig?.enabled && (
                                      <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500 border border-background" />
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs">
                                  {cfg?.filterConfig?.enabled
                                    ? 'Filter active — click to edit'
                                    : 'Set field filter'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>

                        {/* Filter summary row — shown inline below the field row when a filter is active */}
                        {visible && cfg?.filterConfig?.enabled && cfg.filterConfig.conditions.length > 0 && (() => {
                          const cond = cfg.filterConfig.conditions[0];
                          return (
                            <div
                              className="mx-2.5 mb-1.5 px-2 py-1 rounded bg-amber-50 border border-amber-200 dark:bg-amber-950/20 dark:border-amber-800/40 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-950/30 transition-colors"
                              onClick={() => setFilterDialogField(fieldName)}
                            >
                              <div className="flex items-center gap-1.5">
                                <Filter className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">Filter:</span>
                                <span className="text-[10px] text-amber-600 dark:text-amber-500">
                                  <span className="font-medium">{cond.fieldName}</span>
                                  {' '}{cond.operator.replace(/-/g, ' ')}
                                  {cond.value && <span className="font-medium"> "{cond.value}"</span>}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t bg-muted/20">
          <p className="text-[10px] text-muted-foreground">{visibleCount} fields enabled</p>
        </div>
      </div>

      {/* Right Panel – Live Preview */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <h4 className="text-xs font-semibold mb-1">Live Form Preview</h4>
        <LiveFormPreview
          fieldConfigurations={state.fieldConfigurations}
          tabConfigurations={state.tabConfigurations}
          fieldOrder={state.fieldOrder}
          sectionOrder={state.sectionOrder}
          customTabFields={state.customTabFields}
          customTabFieldOrder={state.customTabFieldOrder}
          tabOrder={state.tabOrder}
          onFieldOrderChange={(newOrder) => onChange({ fieldOrder: newOrder })}
          onSectionOrderChange={(newOrder) => onChange({ sectionOrder: newOrder })}
          onCustomTabFieldOrderChange={(newOrder) => onChange({ customTabFieldOrder: newOrder })}
          onTabOrderChange={(newOrder) => onChange({ tabOrder: newOrder })}
          onFieldRemove={(fieldName) =>
            onChange({
              fieldConfigurations: state.fieldConfigurations.map((f) =>
                f.fieldName === fieldName ? { ...f, isVisible: false } : f
              ),
              fieldOrder: state.fieldOrder.filter((n) => n !== fieldName),
              // Also remove from custom tab if it was there
              customTabFields: state.customTabFields.filter((n) => n !== fieldName),
              customTabFieldOrder: state.customTabFieldOrder.filter((n) => n !== fieldName),
            })
          }
          onReset={() =>
            onChange({
              fieldConfigurations: generateDefaultFieldConfigurations(),
              fieldOrder: [],
              sectionOrder: ['schedule', 'resource', 'project-details'],
              customTabFields: [],
              customTabFieldOrder: [],
              tabOrder: generateDefaultTabConfigurations().map((t) => t.tabName),
            })
          }
        />
      </div>

      {/* ── Field Filter Dialog — opens when user clicks the filter icon on a filterable field ── */}
      {filterDialogField && FILTERABLE_FIELDS[filterDialogField] && (
        <FieldFilterDialog
          open={!!filterDialogField}
          onOpenChange={(open) => { if (!open) setFilterDialogField(null); }}
          fieldType={FILTERABLE_FIELDS[filterDialogField]}
          fieldLabel={getFieldConfig(filterDialogField)?.customName || filterDialogField}
          currentFilter={getFieldConfig(filterDialogField)?.filterConfig}
          onApply={(filterConfig) => {
            updateField(filterDialogField, { filterConfig });
            setFilterDialogField(null);
          }}
          customFieldDefinitions={FILTERABLE_FIELDS[filterDialogField] === 'project' ? customFieldOptions : undefined}
          loadingCustomFields={FILTERABLE_FIELDS[filterDialogField] === 'project' ? loadingCustomFields : undefined}
        />
      )}
    </div>
  );
}

// ─── SortableTabCard & Handle (used in Step 4 config panel) ──────────────────

const SortableTabCardHandleContext = React.createContext<React.HTMLAttributes<HTMLButtonElement>>({});

function SortableTabCard({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <SortableTabCardHandleContext.Provider value={{ ...attributes, ...listeners }}>
      <div ref={setNodeRef} style={style}>
        {children}
      </div>
    </SortableTabCardHandleContext.Provider>
  );
}

function SortableTabCardHandle() {
  const handleProps = React.useContext(SortableTabCardHandleContext);
  return (
    <span
      {...handleProps}
      onClick={(e) => e.stopPropagation()}
      className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/80 transition-colors flex-shrink-0 touch-none"
    >
      <GripVertical className="h-3.5 w-3.5" />
    </span>
  );
}

// ─── Step 4: Tab Configuration ────────────────────────────────────────────────

function Step4({
  state,
  onChange,
}: {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}) {
  const [openTab, setOpenTab] = useState<string | null>(null);

  const tabCardSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateTab = (tabName: string, updates: Partial<TabConfiguration>) => {
    onChange({ tabConfigurations: state.tabConfigurations.map((t) => t.tabName === tabName ? { ...t, ...updates } : t) });
  };

  // All fields the user enabled in Step 3
  const CUSTOM_TAB_EXCLUDED = ['From', 'To', 'Booking Type', 'Status', 'Colour'];
  const enabledFields = state.fieldConfigurations.filter(
    (f) => f.isVisible && !CUSTOM_TAB_EXCLUDED.includes(f.fieldName)
  );

  // Is a field currently assigned to the custom tab?
  const isInCustomTab = (fieldName: string) => state.customTabFields.includes(fieldName);

  // Assign a field to the custom tab — removes it from the main fieldOrder display
  const assignToCustomTab = (fieldName: string) => {
    if (isInCustomTab(fieldName)) return;
    onChange({
      customTabFields: [...state.customTabFields, fieldName],
      customTabFieldOrder: [...state.customTabFieldOrder, fieldName],
    });
  };

  // Remove a field from the custom tab — it returns to Project Details
  const removeFromCustomTab = (fieldName: string) => {
    onChange({
      customTabFields: state.customTabFields.filter((n) => n !== fieldName),
      customTabFieldOrder: state.customTabFieldOrder.filter((n) => n !== fieldName),
    });
  };

  return (
    <div className="flex gap-4 h-full min-h-[580px]">
      {/* Left Panel – Tab Config */}
      <div className="w-80 flex-shrink-0 flex flex-col">
        <div className="mb-3">
          <h4 className="text-xs font-semibold">Tab Configuration</h4>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Toggle tab visibility and customise labels. Changes reflect in the live preview.
          </p>
        </div>
        <DndContext
          sensors={tabCardSensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIdx = state.tabOrder.indexOf(active.id as string);
            const newIdx = state.tabOrder.indexOf(over.id as string);
            if (oldIdx === -1 || newIdx === -1) return;
            onChange({ tabOrder: arrayMove(state.tabOrder, oldIdx, newIdx) });
          }}
        >
          <SortableContext items={state.tabOrder.filter((tn) => { if (tn === 'customer-notes') return false; const t = state.tabConfigurations.find((tc) => tc.tabName === tn); return t && (t.customName || t.defaultName); })} strategy={verticalListSortingStrategy}>
        <div className="flex-1 overflow-y-auto space-y-2 pr-0.5">
          {state.tabOrder.map((tabName) => {
            if (tabName === 'customer-notes') return null;
            const tab = state.tabConfigurations.find((t) => t.tabName === tabName);
            if (!tab) return null;
            // Skip tabs with no header text (blank defaultName and no customName)
            const displayName = tab.customName || tab.defaultName;
            if (!displayName) return null;
            const Icon = TAB_ICONS[tab.tabName] ?? <FileText className="h-3.5 w-3.5" />;
            const isOpen = openTab === tab.tabName;
            const isCustom = tab.tabName === 'custom';

            return (
              <SortableTabCard key={tab.tabName} id={tab.tabName}>
              <Card className={cn('transition-all overflow-hidden', tab.isVisible ? 'border-primary/30' : 'opacity-60')}>
                <Collapsible open={isOpen} onOpenChange={() => setOpenTab(isOpen ? null : tab.tabName)}>
                  {/* Card header row */}
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-2">
                        <SortableTabCardHandle />
                        <span className="text-muted-foreground">{Icon}</span>
                        <span className="text-xs font-medium">{displayName}</span>

                        {isCustom && tab.isVisible && state.customTabFields.length > 0 && (
                          <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                            {state.customTabFields.length} field{state.customTabFields.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={tab.isVisible}
                          onCheckedChange={(v) => {
                            updateTab(tab.tabName, { isVisible: v });
                            // Auto-open custom tab config when enabled
                            if (isCustom && v) setOpenTab('custom');
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="scale-75"
                        />
                        <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t">
                      {/* Custom label */}
                      <div className="px-3 pt-2.5 pb-2">
                        <Label className="text-[10px] text-muted-foreground mb-1 block">Custom Label</Label>
                        <Input
                          value={tab.customName || ''}
                          onChange={(e) => updateTab(tab.tabName, { customName: e.target.value })}
                          placeholder={tab.defaultName}
                          className="h-7 text-xs"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Leave blank to use default: "{tab.defaultName}"
                        </p>
                      </div>

                      {/* Custom tab field assignment — only shown when custom tab is enabled */}
                      {isCustom && tab.isVisible && (
                        <div className="border-t px-3 pt-2.5 pb-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] font-semibold text-foreground">Assign Fields</Label>
                            {state.customTabFields.length > 0 && (
                              <span className="text-[10px] text-muted-foreground">
                                {state.customTabFields.length} assigned
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground leading-relaxed">
                            Select enabled fields to display in this tab. Assigned fields are removed from the main form.
                          </p>

                          {enabledFields.length === 0 ? (
                            <p className="text-[10px] text-muted-foreground italic py-1">
                              No fields enabled — go back to Step 3 to enable fields first.
                            </p>
                          ) : (
                            <div className="space-y-1 max-h-40 overflow-y-auto rounded border border-border/60 p-1.5 bg-muted/20">
                              {enabledFields.map((field) => {
                                const assigned = isInCustomTab(field.fieldName);
                                return (
                                  <div
                                    key={field.fieldName}
                                    className={cn(
                                      'flex items-center justify-between rounded px-2 py-1 text-[11px] transition-colors cursor-pointer select-none',
                                      assigned
                                        ? 'bg-primary/10 border border-primary/25 text-primary'
                                        : 'hover:bg-muted/60 text-foreground border border-transparent'
                                    )}
                                    onClick={() => assigned ? removeFromCustomTab(field.fieldName) : assignToCustomTab(field.fieldName)}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {assigned
                                        ? <CheckSquare className="h-3 w-3 flex-shrink-0 text-primary" />
                                        : <div className="h-3 w-3 rounded border border-muted-foreground/40 flex-shrink-0" />
                                      }
                                      <span className="truncate">{field.customName || field.fieldName}</span>
                                    </div>
                                    {assigned && (
                                      <span className="text-[9px] text-primary/70 flex-shrink-0 ml-1">Custom Tab</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Assigned fields reorder hint */}
                          {state.customTabFields.length > 0 && (
                            <p className="text-[10px] text-muted-foreground">
                              Drag fields in the preview to reorder them within the Custom Tab.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
              </SortableTabCard>
            );
          })}
        </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Right Panel – Live Preview */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <h4 className="text-xs font-semibold mb-1">Live Form Preview</h4>
        <LiveFormPreview
          fieldConfigurations={state.fieldConfigurations}
          tabConfigurations={state.tabConfigurations}
          fieldOrder={state.fieldOrder}
          sectionOrder={state.sectionOrder}
          customTabFields={state.customTabFields}
          customTabFieldOrder={state.customTabFieldOrder}
          tabOrder={state.tabOrder}
          onFieldOrderChange={(newOrder) => onChange({ fieldOrder: newOrder })}
          onSectionOrderChange={(newOrder) => onChange({ sectionOrder: newOrder })}
          onCustomTabFieldOrderChange={(newOrder) => onChange({ customTabFieldOrder: newOrder })}
          onTabOrderChange={(newOrder) => onChange({ tabOrder: newOrder })}
          onFieldRemove={(fieldName) =>
            onChange({
              fieldConfigurations: state.fieldConfigurations.map((f) =>
                f.fieldName === fieldName ? { ...f, isVisible: false } : f
              ),
              fieldOrder: state.fieldOrder.filter((n) => n !== fieldName),
              customTabFields: state.customTabFields.filter((n) => n !== fieldName),
              customTabFieldOrder: state.customTabFieldOrder.filter((n) => n !== fieldName),
            })
          }
          onReset={() =>
            onChange({
              fieldConfigurations: generateDefaultFieldConfigurations(),
              fieldOrder: [],
              sectionOrder: ['schedule', 'resource', 'project-details'],
              customTabFields: [],
              customTabFieldOrder: [],
              tabOrder: generateDefaultTabConfigurations().map((t) => t.tabName),
            })
          }
        />
      </div>
    </div>
  );
}

// ─── Wizard ───────────────────────────────────────────────────────────────────

const STEPS = [
  { label: 'Name & Identity', description: 'Basic information' },
  { label: 'General Options', description: 'Behaviour settings' },
  { label: 'Field Selection', description: 'Form fields & preview' },
  { label: 'Tab Configuration', description: 'Tabs & labels' },
];

const DEFAULT_SECTION_ORDER: SectionId[] = ['schedule', 'resource', 'project-details'];

function createInitialWizardState(existing?: BookingType): WizardState {
  if (existing) {
    return {
      id: String(existing.id),
      name: existing.name,
      description: '',
      code: existing.code,
      active: existing.active,
      autoUpdateTimesheet: existing.autoUpdateTimesheet,
      doNotUpdateTimesheetWithHours: existing.doNotUpdateTimesheetWithHours,
      obeyUserWorkingProfileAndBankHolidays: existing.obeyUserWorkingProfileAndBankHolidays,
      autoAdjustBookingForWorkingProfileAndBankHolidays: existing.autoAdjustBookingForWorkingProfileAndBankHolidays,
      addDaysToEndOfBooking: existing.addDaysToEndOfBooking,
      allowRepeatBookings: existing.allowRepeatBookings,
      fieldConfigurations: existing.fieldConfigurations || generateDefaultFieldConfigurations(),
      tabConfigurations: (existing.tabConfigurations || generateDefaultTabConfigurations()).filter((t) => t.customName || t.defaultName),
      fieldOrder: existing.fieldOrder || generateDefaultFieldOrder(),
      sectionOrder: DEFAULT_SECTION_ORDER,
      customTabFields: [],
      customTabFieldOrder: [],
      tabOrder: (existing.tabConfigurations || generateDefaultTabConfigurations()).filter((t) => t.customName || t.defaultName).map((t) => t.tabName),
    };
  }
  const defaultTabs = generateDefaultTabConfigurations();
  return {
    name: '',
    description: '',
    code: '',
    active: true,
    autoUpdateTimesheet: true,
    doNotUpdateTimesheetWithHours: false,
    obeyUserWorkingProfileAndBankHolidays: true,
    autoAdjustBookingForWorkingProfileAndBankHolidays: false,
    addDaysToEndOfBooking: false,
    allowRepeatBookings: true,
    fieldConfigurations: generateDefaultFieldConfigurations(),
    tabConfigurations: defaultTabs,
    fieldOrder: generateDefaultFieldOrder(),
    sectionOrder: DEFAULT_SECTION_ORDER,
    customTabFields: [],
    customTabFieldOrder: [],
    tabOrder: defaultTabs.map((t) => t.tabName),
  };
}

interface WizardProps {
  initial?: BookingType;
  existingBookingTypes: BookingType[];
  onSave: (state: WizardState) => Promise<void>;
  onCancel: () => void;
  tallMode: boolean;
  onToggleTallMode: () => void;
  isSaving?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

function BookingTypeWizard({ initial, existingBookingTypes, onSave, onCancel, tallMode, onToggleTallMode, isSaving, onDirtyChange }: WizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const initialState = useMemo(() => createInitialWizardState(initial), [initial]);
  const [state, setState] = useState<WizardState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Report dirty state to the parent so it can guard cancel/back navigation.
  const isDirty = useMemo(() => JSON.stringify(state) !== JSON.stringify(initialState), [state, initialState]);
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const onChange = useCallback((updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
    if (updates.name !== undefined || updates.code !== undefined) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.name;
        delete next.code;
        return next;
      });
    }
  }, []);

  const validateStep = (s: WizardStep): boolean => {
    const newErrors: Record<string, string> = {};
    if (s === 1) {
      if (!state.name.trim()) {
        newErrors.name = 'Booking type name is required';
      } else {
        const trimmedName = state.name.trim().toLowerCase();
        const trimmedCode = (state.code.trim() || state.name.slice(0, 8).toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '')).toLowerCase();

        const otherBookingTypes = existingBookingTypes.filter(
          (bt) => String(bt.id) !== String(state.id)
        );

        const nameConflict = otherBookingTypes.find(
          (bt) => bt.name.trim().toLowerCase() === trimmedName
        );
        if (nameConflict) {
          newErrors.name = 'A booking type with this name already exists';
        }

        const codeConflict = otherBookingTypes.find(
          (bt) => bt.code.trim().toLowerCase() === trimmedCode
        );
        if (codeConflict) {
          newErrors.code = 'This internal code is already in use';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    if (step < 4) setStep((s) => (s + 1) as WizardStep);
  };

  const goPrev = () => {
    if (step > 1) setStep((s) => (s - 1) as WizardStep);
  };

  const handleSave = async () => {
    if (!validateStep(1)) { setStep(1); return; }
    // Auto-generate code if blank
    const finalState = {
      ...state,
      code: state.code.trim() || state.name.slice(0, 6).toUpperCase().replace(/\s+/g, ''),
    };
    await onSave(finalState);
  };

  const progressPercent = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="flex flex-col h-full">
      {/* Progress Bar */}
      <div className="px-6 pt-4 pb-2 border-b bg-muted/20">
        <div className="flex items-center justify-between mb-3">
          {STEPS.map((s, i) => {
            const stepNum = (i + 1) as WizardStep;
            const isActive = step === stepNum;
            const isDone = step > stepNum;
            return (
              <div key={i} className="flex items-center gap-2 flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all',
                      isDone
                        ? 'bg-primary border-primary text-primary-foreground'
                        : isActive
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-background border-muted-foreground/30 text-muted-foreground'
                    )}
                  >
                    {isDone ? <Check className="h-3.5 w-3.5" /> : stepNum}
                  </div>
                  <div className="mt-1 text-center">
                    <div className={cn('text-[10px] font-medium leading-tight', isActive ? 'text-primary' : 'text-muted-foreground')}>
                      {s.label}
                    </div>
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={cn('flex-1 h-0.5 mx-1 mb-4 transition-colors', step > stepNum ? 'bg-primary' : 'bg-muted-foreground/20')} />
                )}
              </div>
            );
          })}
        </div>
        <Progress value={progressPercent} className="h-1" />
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] text-muted-foreground">
            Step {step} of {STEPS.length} — {STEPS[step - 1].description}
          </p>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={onToggleTallMode}
                >
                  {tallMode
                    ? <Minimize2 className="h-3.5 w-3.5" />
                    : <Maximize2 className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                {tallMode ? 'Exit full-page mode' : 'Expand to full page'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {step === 1 && (
          <div>
            <Step1 state={state} onChange={onChange} errors={errors} />
          </div>
        )}
        {step === 2 && <Step2 state={state} onChange={onChange} />}
        {step === 3 && <Step3 state={state} onChange={onChange} />}
        {step === 4 && <Step4 state={state} onChange={onChange} />}
      </div>

      {/* Footer Navigation */}
      <div className="px-6 py-4 border-t bg-muted/10 flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="gap-1.5">
          <X className="h-4 w-4" />
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={goPrev} className="gap-1.5">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          {step < 4 ? (
            <Button onClick={goNext} className="gap-1.5">
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSave} className="gap-1.5 bg-primary" disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {isSaving ? 'Saving...' : 'Save Booking Type'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────

interface ListViewProps {
  bookingTypes: BookingType[];
  bookings: { bookingTypeId?: string }[];
  auditLog: Record<string, AuditEntry[]>;
  resources: Resource[];
  permissions: Record<string, Record<string, PermissionLevel>>;
  onPermissionsChange: (bookingTypeId: string, resourceId: string, level: PermissionLevel) => void;
  onBulkPermissionsChange: (bookingTypeId: string, resourceIds: string[], level: PermissionLevel) => void;
  onEdit: (bt: BookingType) => void;
  onDelete: (id: string) => void;
  onCreate: () => void;
  onToggleActive: (id: string, active: boolean) => void;
  highlightedBookingTypeId?: string | null;
}

function BookingTypeListView({ bookingTypes, bookings, auditLog, resources, permissions, onPermissionsChange, onBulkPermissionsChange, onEdit, onDelete, onCreate, onToggleActive, highlightedBookingTypeId }: ListViewProps) {
  const [deleteTarget, setDeleteTarget] = useState<BookingType | null>(null);
  const [historyTarget, setHistoryTarget] = useState<BookingType | null>(null);
  const [usageTarget, setUsageTarget] = useState<BookingType | null>(null);
  const [usageSearch, setUsageSearch] = useState('');
  const [usagePage, setUsagePage] = useState(1);
  const USAGE_PAGE_SIZE = 10;
  const highlightRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Scroll to the highlighted booking type when it changes
  useEffect(() => {
    if (highlightedBookingTypeId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedBookingTypeId]);

  const getUsageCount = useCallback(
    (btId: string) => bookings.filter((b) => String(b.bookingTypeId) === String(btId)).length,
    [bookings]
  );

  const historyEntries: AuditEntry[] = historyTarget
    ? (auditLog[String(historyTarget.id)] ?? []).slice().reverse()
    : [];

  const filteredBookingTypes = searchQuery.trim()
    ? bookingTypes.filter((bt) => {
        const q = searchQuery.toLowerCase();
        return (
          bt.name?.toLowerCase().includes(q) ||
          bt.code?.toLowerCase().includes(q)
        );
      })
    : bookingTypes;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Booking Types</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {searchQuery.trim()
              ? `${filteredBookingTypes.length} of ${bookingTypes.length} booking type${bookingTypes.length !== 1 ? 's' : ''}`
              : `${bookingTypes.length} booking type${bookingTypes.length !== 1 ? 's' : ''} configured`}
          </p>
        </div>
        <Button onClick={onCreate} className="gap-1.5" size="sm">
          <Plus className="h-4 w-4" />
          Create New Booking Type
        </Button>
      </div>

      {/* Search bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search by code or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      {bookingTypes.length === 0 ? (
        <div className="border rounded-lg p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
            <FileText className="h-6 w-6 text-muted-foreground" />
          </div>
          <h4 className="text-sm font-medium mb-1">No booking types yet</h4>
          <p className="text-xs text-muted-foreground mb-4">Create your first booking type to get started</p>
          <Button onClick={onCreate} size="sm" className="gap-1.5">
            <Plus className="h-4 w-4" />
            Create New Booking Type
          </Button>
        </div>
      ) : filteredBookingTypes.length === 0 ? (
        <div className="border rounded-lg p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
            <Search className="h-6 w-6 text-muted-foreground" />
          </div>
          <h4 className="text-sm font-medium mb-1">No booking types match your search</h4>
          <p className="text-xs text-muted-foreground mb-4">Try a different code or name</p>
          <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
            Clear search
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          {/* Table Header — Code · Name · Description · Status · Actions */}
          <div className="grid grid-cols-[120px_1fr_2fr_110px_120px] gap-3 px-4 py-2.5 bg-muted/50 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
            <div>Code</div>
            <div>Name</div>
            <div>Description</div>
            <div className="text-center">Status</div>
            <div className="text-center">Actions</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y">
            {filteredBookingTypes.map((bt) => {
              const usageCount = getUsageCount(String(bt.id));
              const canDelete = usageCount === 0;
              const description = bt.description;
              const hasHistory = (auditLog[String(bt.id)] ?? []).length > 0;
              const isHighlighted = highlightedBookingTypeId === String(bt.id);
              return (
                <div
                  key={bt.id}
                  ref={isHighlighted ? highlightRef : undefined}
                  className={cn(
                    'grid grid-cols-[120px_1fr_2fr_110px_120px] gap-3 px-4 py-3 items-center transition-colors',
                    isHighlighted
                      ? 'bg-primary/10 ring-2 ring-primary/30 ring-inset animate-pulse'
                      : 'hover:bg-muted/20'
                  )}
                >
                  {/* Code */}
                  <div>
                    {bt.code ? (
                      <span className="inline-flex items-center gap-1 font-mono text-xs bg-muted px-2 py-0.5 rounded border">
                        <Hash className="h-3 w-3 text-muted-foreground" />
                        {bt.code}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50 italic">—</span>
                    )}
                  </div>

                  {/* Name */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{bt.name}</span>
                  </div>

                  {/* Description */}
                  <div className="min-w-0">
                    {description ? (
                      <span className="text-xs text-muted-foreground truncate block">{description}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground/40 italic">No description</span>
                    )}
                  </div>

                  {/* Status — click to toggle */}
                  <div className="flex justify-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onToggleActive(String(bt.id), !bt.active)}
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium border transition-colors cursor-pointer select-none',
                              bt.active
                                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                            )}
                          >
                            <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', bt.active ? 'bg-green-500' : 'bg-muted-foreground/50')} />
                            {bt.active ? 'Active' : 'Inactive'}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{bt.active ? 'Click to set inactive' : 'Click to set active'}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-center gap-0.5">
                    {/* Usage / Permissions */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-primary"
                            onClick={() => { setUsageTarget(bt); setUsageSearch(''); setUsagePage(1); }}
                          >
                            <User className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Manage user access</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* History */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn('h-7 w-7', hasHistory ? 'text-primary' : 'text-muted-foreground')}
                            onClick={() => setHistoryTarget(bt)}
                          >
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{hasHistory ? 'View audit history' : 'No history yet'}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Edit */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(bt)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit booking type</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Delete */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn('h-7 w-7', canDelete ? 'text-destructive hover:text-destructive hover:bg-destructive/10' : 'opacity-40 cursor-not-allowed')}
                            onClick={() => canDelete && setDeleteTarget(bt)}
                            disabled={!canDelete}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {canDelete ? 'Delete booking type' : `Cannot delete — used in ${usageCount} booking${usageCount !== 1 ? 's' : ''}`}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Usage / Permissions Dialog ── */}
      <Dialog open={!!usageTarget} onOpenChange={(open) => { if (!open) setUsageTarget(null); }}>
        <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              User Access — {usageTarget?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by resource name..."
              value={usageSearch}
              onChange={(e) => { setUsageSearch(e.target.value); setUsagePage(1); }}
              className="pl-9"
            />
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground px-1">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />Read only</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Full access</span>
          </div>

          {/* Table + Paging */}
          {(() => {
            const btId = String(usageTarget?.id ?? '');
            const allResources = resources.filter(r => String(r.id) !== 'unallocated');
            const filtered = allResources.filter(r =>
              !usageSearch.trim() || r.name.toLowerCase().includes(usageSearch.toLowerCase())
            );

            const totalPages = Math.max(1, Math.ceil(filtered.length / USAGE_PAGE_SIZE));
            const safePage = Math.min(usagePage, totalPages);
            const pageStart = (safePage - 1) * USAGE_PAGE_SIZE;
            const paginated = filtered.slice(pageStart, pageStart + USAGE_PAGE_SIZE);

            // Bulk-state helpers operate on ALL filtered rows (not just the current page)
            const allReadOnly   = filtered.length > 0 && filtered.every(r => (permissions[btId]?.[String(r.id)] ?? 'none') === 'read-only');
            const allFullAccess = filtered.length > 0 && filtered.every(r => (permissions[btId]?.[String(r.id)] ?? 'none') === 'full-access');
            const setAll = (level: PermissionLevel) => {
              const resourceIds = filtered.map(r => String(r.id));
              onBulkPermissionsChange(btId, resourceIds, level);
            };

            return (
              <>
                <div className="flex-1 overflow-y-auto border rounded-lg">
                  {/* Sticky header with select-all buttons */}
                  <div className="grid grid-cols-[1fr_110px_110px] gap-2 px-4 py-2.5 bg-muted border-b text-xs font-medium text-muted-foreground uppercase tracking-wide sticky top-0 z-10">
                    <div>Resource</div>

                    {/* Read Only — select all */}
                    <div className="flex flex-col items-center gap-1">
                      <span>Read Only</span>
                      <button
                        onClick={() => setAll(allReadOnly ? 'none' : 'read-only')}
                        title={allReadOnly ? 'Clear read only for all' : 'Set all to read only'}
                        className={cn(
                          'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
                          allReadOnly ? 'border-blue-500 bg-blue-50' : 'border-border hover:border-blue-400'
                        )}
                      >
                        {allReadOnly && <Eye className="h-3 w-3 text-blue-600" />}
                      </button>
                    </div>

                    {/* Full Access — select all */}
                    <div className="flex flex-col items-center gap-1">
                      <span>Full Access</span>
                      <button
                        onClick={() => setAll(allFullAccess ? 'none' : 'full-access')}
                        title={allFullAccess ? 'Clear full access for all' : 'Set all to full access'}
                        className={cn(
                          'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
                          allFullAccess ? 'border-green-500 bg-green-50' : 'border-border hover:border-green-400'
                        )}
                      >
                        {allFullAccess && <Check className="h-3 w-3 text-green-600" />}
                      </button>
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                      {usageSearch.trim() ? 'No resources match your search.' : 'No resources available.'}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {paginated.map((resource) => {
                        const current: PermissionLevel = permissions[btId]?.[String(resource.id)] ?? 'none';
                        const set = (level: PermissionLevel) => onPermissionsChange(btId, String(resource.id), level);
                        return (
                          <div key={resource.id} className="grid grid-cols-[1fr_110px_110px] gap-2 px-4 py-2.5 items-center hover:bg-muted/20 transition-colors">
                            {/* Resource info */}
                            <div className="flex items-center gap-2 min-w-0">
                              {resource.photoUrl ? (
                                <img src={resource.photoUrl} alt={resource.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <User className="h-3.5 w-3.5 text-primary" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{resource.name}</p>
                                {resource.role && <p className="text-xs text-muted-foreground truncate">{resource.role}</p>}
                              </div>
                            </div>

                            {/* Read Only */}
                            <div className="flex justify-center">
                              <button
                                onClick={() => set(current === 'read-only' ? 'none' : 'read-only')}
                                className={cn(
                                  'w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all',
                                  current === 'read-only' ? 'border-blue-500 bg-blue-50' : 'border-border hover:border-blue-400'
                                )}
                                aria-label="Read only"
                              >
                                {current === 'read-only' && <Eye className="h-3.5 w-3.5 text-blue-600" />}
                              </button>
                            </div>

                            {/* Full Access */}
                            <div className="flex justify-center">
                              <button
                                onClick={() => set(current === 'full-access' ? 'none' : 'full-access')}
                                className={cn(
                                  'w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all',
                                  current === 'full-access' ? 'border-green-500 bg-green-50' : 'border-border hover:border-green-400'
                                )}
                                aria-label="Full access"
                              >
                                {current === 'full-access' && <Check className="h-3.5 w-3.5 text-green-600" />}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Pagination footer — only shown when there is more than one page */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2 text-sm">
                    <span className="text-xs text-muted-foreground">
                      {pageStart + 1}–{Math.min(pageStart + USAGE_PAGE_SIZE, filtered.length)} of {filtered.length} users
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={safePage === 1}
                        onClick={() => setUsagePage(p => p - 1)}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={page === safePage ? 'default' : 'outline'}
                          size="icon"
                          className="h-7 w-7 text-xs"
                          onClick={() => setUsagePage(page)}
                          aria-label={`Page ${page}`}
                        >
                          {page}
                        </Button>
                      ))}
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={safePage === totalPages}
                        onClick={() => setUsagePage(p => p + 1)}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Audit History Dialog ── */}
      <Dialog open={!!historyTarget} onOpenChange={(open) => !open && setHistoryTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-primary" />
              Audit History — {historyTarget?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1 mt-1">
            {historyEntries.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                No history recorded yet. Changes will appear here after the first save.
              </div>
            ) : (
              historyEntries.map((entry) => (
                <div key={entry.id} className="border rounded-lg overflow-hidden">
                  {/* Entry header */}
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        'inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5',
                        entry.action === 'created' ? 'bg-green-100 text-green-700' :
                        entry.action === 'updated' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      )}>
                        {entry.action === 'created' && <Plus className="h-2.5 w-2.5" />}
                        {entry.action === 'updated' && <Pencil className="h-2.5 w-2.5" />}
                        {entry.action === 'deleted' && <Trash2 className="h-2.5 w-2.5" />}
                        {entry.action}
                      </div>
                      <span className="text-xs font-medium">{entry.user}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>

                  {/* Changes table */}
                  {entry.changes.length > 0 ? (
                    <div className="divide-y">
                      {/* Column headers */}
                      <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-3 py-1.5 bg-muted/20 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">
                        <div>Field</div>
                        <div>Before</div>
                        <div>After</div>
                      </div>
                      {entry.changes.map((ch, i) => (
                        <div key={i} className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-3 py-2 text-xs items-start">
                          <div className="font-medium text-foreground/80 leading-snug">{ch.field}</div>
                          <div className="text-muted-foreground leading-snug">
                            {ch.oldValue === null ? (
                              <span className="italic text-muted-foreground/50">—</span>
                            ) : (
                              <span className="line-through decoration-muted-foreground/40">{ch.oldValue}</span>
                            )}
                          </div>
                          <div className="text-foreground leading-snug font-medium">
                            {ch.newValue === null ? (
                              <span className="italic text-muted-foreground/50">—</span>
                            ) : ch.newValue}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="px-3 py-2 text-xs text-muted-foreground italic">No field changes recorded.</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) {
                  onDelete(String(deleteTarget.id));
                  setDeleteTarget(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

type ViewMode = 'list' | 'wizard';

export function BookingTypeSetup({
  bookingTypes = [],
  bookings = [],
  resources = [],
  onSave,
  onDelete,
  dataMode,
  demoBookingTypeUsages,
  onDemoUsageChange,
  onDemoBulkUsageChange,
}: BookingTypeSetupProps) {
  const audit = useAudit();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingBookingType, setEditingBookingType] = useState<BookingType | undefined>(undefined);
  const [tallMode, setTallMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [wizardDirty, setWizardDirty] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [highlightedBookingTypeId, setHighlightedBookingTypeId] = useState<string | null>(null);
  // bookingTypePermissions: { [bookingTypeId]: { [resourceId]: PermissionLevel } }
  const [bookingTypePermissions, setBookingTypePermissions] = useState<Record<string, Record<string, PermissionLevel>>>({});

  // Load existing BookingTypeUsages from API on mount (or from demo state)
  useEffect(() => {
    if (dataMode === 'demo' && demoBookingTypeUsages) {
      const perms: Record<string, Record<string, PermissionLevel>> = {};
      for (const u of demoBookingTypeUsages) {
        const btId = String(u.bookingTypeId);
        const resId = String(u.resourceId);
        if (!perms[btId]) perms[btId] = {};
        if (u.isFullAccess) {
          perms[btId][resId] = 'full-access';
        } else if (u.isReadOnly) {
          perms[btId][resId] = 'read-only';
        } else {
          perms[btId][resId] = 'none';
        }
      }
      setBookingTypePermissions(perms);
      return;
    }

    bookingTypeUsagesApi.getAll()
      .then((usages: BookingTypeUsageDto[]) => {
        const perms: Record<string, Record<string, PermissionLevel>> = {};
        for (const u of usages) {
          const btId = String(u.bookingTypeId);
          const resId = String(u.resourceId);
          if (!perms[btId]) perms[btId] = {};
          if (u.isFullAccess) {
            perms[btId][resId] = 'full-access';
          } else if (u.isReadOnly) {
            perms[btId][resId] = 'read-only';
          } else {
            perms[btId][resId] = 'none';
          }
        }
        setBookingTypePermissions(perms);
      })
      .catch(() => { /* silently ignore — permissions just won't be pre-populated */ });
  }, [dataMode, demoBookingTypeUsages]);

  const handlePermissionsChange = (bookingTypeId: string, resourceId: string, level: PermissionLevel) => {
    // Update local state immediately for responsive UI
    setBookingTypePermissions(prev => ({
      ...prev,
      [bookingTypeId]: {
        ...(prev[bookingTypeId] ?? {}),
        [resourceId]: level,
      },
    }));

    // In demo mode, update demo usages via callback
    if (dataMode === 'demo' && onDemoUsageChange) {
      onDemoUsageChange(bookingTypeId, resourceId, level);
      return;
    }

    // Persist to database via API
    const resId = Number(resourceId);
    const btId = Number(bookingTypeId);
    if (isNaN(resId) || isNaN(btId) || resId <= 0 || btId <= 0) return;

    bookingTypeUsagesApi.upsert({
      resourceId: resId,
      bookingTypeId: btId,
      isReadOnly: level === 'read-only',
      isFullAccess: level === 'full-access',
    }).catch((err) => {
      // Revert optimistic local state so UI reflects the actual server state
      setBookingTypePermissions(prev => {
        const reverted = { ...(prev[bookingTypeId] ?? {}) };
        delete reverted[resourceId];
        return { ...prev, [bookingTypeId]: reverted };
      });
      const detail = err instanceof Error ? err.message : '';
      toast.error('Failed to save permission', {
        description: detail || 'Could not save the access level. Please try again.',
      });
    });
  };

  const handleBulkPermissionsChange = (bookingTypeId: string, resourceIds: string[], level: PermissionLevel) => {
    // Update local state for all resources at once
    setBookingTypePermissions(prev => {
      const updated = { ...(prev[bookingTypeId] ?? {}) };
      for (const rid of resourceIds) {
        updated[rid] = level;
      }
      return { ...prev, [bookingTypeId]: updated };
    });

    // In demo mode, update demo usages via callback
    if (dataMode === 'demo' && onDemoBulkUsageChange) {
      onDemoBulkUsageChange(bookingTypeId, resourceIds, level);
      return;
    }

    // Build payload for API — only include resources with valid numeric IDs
    const btId = Number(bookingTypeId);
    if (isNaN(btId) || btId <= 0) return;

    const payload = resourceIds
      .map(rid => ({ resourceId: Number(rid), bookingTypeId: btId, isReadOnly: level === 'read-only', isFullAccess: level === 'full-access' }))
      .filter(item => !isNaN(item.resourceId) && item.resourceId > 0);

    if (payload.length === 0) return;

    // Try bulk upsert first; if it fails, fall back to individual upserts
    bookingTypeUsagesApi.bulkUpsert(payload).catch(() => {
      // Bulk endpoint may not be available — fall back to individual upserts
      const upsertPromises = payload.map(item =>
        bookingTypeUsagesApi.upsert(item).catch(() => null)
      );
      return Promise.all(upsertPromises).then(results => {
        const failures = results.filter(r => r === null).length;
        if (failures > 0 && failures === results.length) {
          // All individual upserts failed — revert optimistic state
          setBookingTypePermissions(prev => {
            const reverted = { ...(prev[bookingTypeId] ?? {}) };
            for (const rid of resourceIds) {
              delete reverted[rid];
            }
            return { ...prev, [bookingTypeId]: reverted };
          });
          toast.error('Failed to save permissions', {
            description: 'Could not save access levels. Please try again.',
          });
        } else if (failures > 0) {
          toast.warning('Some permissions could not be saved', {
            description: `${failures} of ${results.length} resources could not be updated.`,
          });
        }
      });
    });
  };

  useEffect(() => {
    document.body.style.overflow = tallMode ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [tallMode]);
  const [auditLog, setAuditLog] = useState<Record<string, AuditEntry[]>>({});

  // Clear highlight after a delay
  useEffect(() => {
    if (highlightedBookingTypeId) {
      const timer = setTimeout(() => setHighlightedBookingTypeId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedBookingTypeId]);

  const handleCreate = () => {
    setEditingBookingType(undefined);
    setViewMode('wizard');
  };

  const handleEdit = (bt: BookingType) => {
    setEditingBookingType(bt);
    setViewMode('wizard');
  };

  const handleWizardSave = async (wizardState: WizardState) => {
    // Map wizard section IDs → BookingFormPreview section IDs
    const sectionIdMap: Record<string, string> = {
      'schedule': 'datetime',
      'resource': 'resources',
      'project-details': 'booking-details',
    };
    const mappedSectionOrder = wizardState.sectionOrder.map(s => sectionIdMap[s] ?? s);

    // Ensure fieldConfigurations have all flags set correctly, including
    // showOnCustomTab for fields assigned to the custom tab
    const mappedFieldConfigs: FieldConfiguration[] = wizardState.fieldConfigurations.map(fc => ({
      ...fc,
      isRequired: fc.isCompulsory ?? fc.isRequired ?? false,
      isCompulsory: fc.isCompulsory ?? false,
      showOnCustomTab: wizardState.customTabFields.includes(fc.fieldName),
    }));

    // Apply tab order from wizard to tabConfigurations displayOrder
    const orderedTabConfigs = wizardState.tabOrder.length > 0
      ? [...(wizardState.tabConfigurations ?? [])].sort((a, b) => {
          const ai = wizardState.tabOrder.indexOf(a.tabName);
          const bi = wizardState.tabOrder.indexOf(b.tabName);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        })
      : wizardState.tabConfigurations;

    // fieldOrder: exclude custom-tab-only fields so they don't also render in Project Details
    const mainFieldOrder = wizardState.fieldOrder.filter(
      f => !wizardState.customTabFields.includes(f)
    );

    const bookingType: BookingType = {
      id: wizardState.id ? (wizardState.id as unknown as number) : (Date.now() as unknown as number),
      code: wizardState.code,
      name: wizardState.name,
      description: wizardState.description,
      active: wizardState.active,
      autoUpdateTimesheet: wizardState.autoUpdateTimesheet,
      doNotUpdateTimesheetWithHours: wizardState.doNotUpdateTimesheetWithHours,
      obeyUserWorkingProfileAndBankHolidays: wizardState.obeyUserWorkingProfileAndBankHolidays,
      autoAdjustBookingForWorkingProfileAndBankHolidays: wizardState.autoAdjustBookingForWorkingProfileAndBankHolidays,
      addDaysToEndOfBooking: wizardState.addDaysToEndOfBooking,
      allowRepeatBookings: wizardState.allowRepeatBookings,
      fieldConfigurations: mappedFieldConfigs,
      tabConfigurations: orderedTabConfigs,
      fieldOrder: mainFieldOrder,
      sectionOrder: mappedSectionOrder,
    };

    // Show loading indicator and await the API response
    setIsSaving(true);
    try {
      await onSave(bookingType);

      // ── Record audit entry (only on success) ─────────────────────────────
      const btId = String(bookingType.id);
      const isNew = !wizardState.id;
      const previousVersion = editingBookingType;
      const changes = diffBookingType(previousVersion, bookingType, wizardState);
      const auditEntry: AuditEntry = {
        id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        bookingTypeId: btId,
        timestamp: new Date().toISOString(),
        user: 'Current User',   // replaced by real auth when permissions are wired
        action: isNew ? 'created' : 'updated',
        changes,
      };
      setAuditLog(prev => ({
        ...prev,
        [btId]: [...(prev[btId] ?? []), auditEntry],
      }));

      audit.log({
        user: 'Current User',
        category: 'Settings – Booking Types',
        action: isNew ? 'New' : 'Edit',
        entity: bookingType.name,
        changes: changes.map(c => ({ field: c.field, before: c.oldValue, after: c.newValue })),
      });

      toast.success(wizardState.id ? 'Booking type updated' : 'Booking type created', {
        description: `"${wizardState.name}" has been ${wizardState.id ? 'updated' : 'created'} successfully.`,
      });

      // Navigate to list and highlight the newly created/updated booking type
      setHighlightedBookingTypeId(btId);
      setViewMode('list');
      setEditingBookingType(undefined);
      setTallMode(false);
      setWizardDirty(false);
    } catch (err) {
      toast.error('Failed to save booking type', {
        description: 'An error occurred while saving. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const performWizardCancel = () => {
    setShowDiscardDialog(false);
    setWizardDirty(false);
    setViewMode('list');
    setEditingBookingType(undefined);
    setTallMode(false);
  };

  const handleWizardCancel = () => {
    if (wizardDirty) {
      setShowDiscardDialog(true);
      return;
    }
    performWizardCancel();
  };

  const handleDelete = (id: string) => {
    onDelete(id);
    toast.success('Booking type deleted');
  };

  const handleToggleActive = (id: string, active: boolean) => {
    const bt = bookingTypes.find((b) => String(b.id) === id);
    if (!bt) return;
    onSave({ ...bt, active });
    toast.success(active ? 'Booking type set to active' : 'Booking type set to inactive');
  };

  if (viewMode === 'wizard') {
    const wizardContent = (
      <div
        className="flex flex-col"
        style={{ minHeight: tallMode ? undefined : '700px', height: tallMode ? '100%' : undefined }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={handleWizardCancel} className="gap-1.5 h-8" disabled={isSaving}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to list
          </Button>
          <Separator orientation="vertical" className="h-4" />
          <h3 className="text-sm font-semibold text-muted-foreground">
            {editingBookingType ? `Editing: ${editingBookingType.name}` : 'Create New Booking Type'}
          </h3>
          {isSaving && (
            <div className="flex items-center gap-2 ml-auto text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </div>
          )}
        </div>

        {/* Wizard card */}
        <div className="border rounded-lg flex-1 overflow-hidden bg-background min-h-0">
          <BookingTypeWizard
            initial={editingBookingType}
            existingBookingTypes={bookingTypes}
            onSave={handleWizardSave}
            onCancel={handleWizardCancel}
            tallMode={tallMode}
            onToggleTallMode={() => setTallMode((v) => !v)}
            isSaving={isSaving}
            onDirtyChange={setWizardDirty}
          />
        </div>
      </div>
    );

    return (
      <>
        {/* Always-mounted wizard — rendered inline so React never unmounts it when tallMode toggles */}
        <div
          className={cn(
            tallMode
              ? 'fixed inset-0 z-[9999] bg-background p-6 overflow-hidden flex flex-col'
              : 'relative'
          )}
          style={tallMode ? { animation: 'fadeIn 0.15s ease' } : { minHeight: '700px' }}
        >
          {tallMode && (
            <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
          )}
          {wizardContent}
        </div>

        {/* Placeholder prevents the settings panel from collapsing when in full-page mode */}
        {tallMode && <div style={{ minHeight: '700px' }} aria-hidden="true" />}

        <DiscardChangesDialog
          open={showDiscardDialog}
          onOpenChange={setShowDiscardDialog}
          onConfirm={performWizardCancel}
        />
      </>
    );
  }

  return (
    <BookingTypeListView
      auditLog={auditLog}
      bookingTypes={bookingTypes}
      bookings={bookings}
      resources={resources}
      permissions={bookingTypePermissions}
      onPermissionsChange={handlePermissionsChange}
      onBulkPermissionsChange={handleBulkPermissionsChange}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onCreate={handleCreate}
      onToggleActive={handleToggleActive}
      highlightedBookingTypeId={highlightedBookingTypeId}
    />
  );
}
